import React, { useState, useEffect } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Gift, Sparkles } from "lucide-react";
import FinlyLogoMark from "@/components/FinlyLogoMark";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [testerTag, setTesterTag] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
    const promo = searchParams.get("promo");
    if (promo) setPromoCode(promo.toUpperCase());
    const tag = searchParams.get("t");
    if (tag) setTesterTag(tag.slice(0, 48));
  }, [searchParams]);

  if (user && typeof user === "object") return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    const res = await register(email, password, name, referralCode, promoCode, testerTag);
    setLoading(false);
    if (res.ok) {
      toast.success("¡Cuenta creada con éxito!");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Error al crear cuenta");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="register-page">
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12 bg-[#0B1220]">
        <div className="max-w-md w-full mx-auto">
          <div className="flex flex-col items-start gap-4 mb-12">
            <FinlyLogoMark size={120} />
            <span
              className="finly-tagline-animated inline-flex items-center gap-2 text-[18px] font-medium text-[#8A9A5B]"
              data-testid="register-tagline"
            >
              Tu dinero, en orden
              <svg
                className="finly-tagline-check w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8A9A5B"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12l4.5 4.5L19 7" />
              </svg>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#F1F5F9] mb-3">
            Crear cuenta
          </h1>
          <p className="text-[#94A3B8] mb-10 text-base leading-relaxed">
            Comienza a tomar control de tus finanzas hoy mismo.
          </p>

          {promoCode && (
            <div
              className="mb-8 rounded-xl border border-[#FFD54A]/40 bg-gradient-to-r from-[#FFD54A]/10 to-[#00C97D]/10 px-5 py-4 flex items-start gap-3"
              data-testid="register-promo-banner"
            >
              <Sparkles className="w-5 h-5 text-[#FFD54A] mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-[#FFE48A]">
                  Acceso Premium para Testers activado
                </p>
                <p className="text-xs text-[#CBD5E1] leading-relaxed">
                  Al crear tu cuenta con el código <span className="font-mono text-[#FFD54A]">{promoCode}</span> tendrás 90 días de Premium sin costo: OCR de recibos, captura por voz ilimitada, Finly Score, exportación PDF y todo lo demás.
                </p>
                {testerTag && (
                  <p
                    className="text-[11px] text-[#94A3B8] mt-1"
                    data-testid="register-tester-tag"
                  >
                    Identificador: <span className="font-mono text-[#CBD5E1]">{testerTag}</span>
                  </p>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                Nombre
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="María García"
                className="mt-2 h-12 bg-[#131C2E] border-[#2A3447] text-[#E2E8F0] placeholder:text-[#64748B] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF] rounded-md"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                Correo
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="tu@correo.com"
                className="mt-2 h-12 bg-[#131C2E] border-[#2A3447] text-[#E2E8F0] placeholder:text-[#64748B] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF] rounded-md"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Mínimo 6 caracteres"
                className="mt-2 h-12 bg-[#131C2E] border-[#2A3447] text-[#E2E8F0] placeholder:text-[#64748B] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF] rounded-md"
                data-testid="register-password-input"
              />
            </div>
            <div>
              <Label htmlFor="referral" className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium flex items-center gap-1.5">
                <Gift className="w-3 h-3 text-[#00C97D]" />
                Código de invitación <span className="normal-case tracking-normal text-[#94A3B8] text-[10px]">(opcional)</span>
              </Label>
              <Input
                id="referral"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Ej. ABC12345"
                maxLength={16}
                className="mt-2 h-12 bg-[#131C2E] border-[#2A3447] text-[#E2E8F0] placeholder:text-[#64748B] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF] rounded-md tracking-widest font-mono"
                data-testid="register-referral-input"
              />
              {referralCode && (
                <p className="mt-1.5 text-[11px] text-[#00C97D] flex items-center gap-1" data-testid="register-referral-applied">
                  <Gift className="w-3 h-3" /> Invitación aplicada — tu amig@ ganará 1 mes gratis cuando te suscribas.
                </p>
              )}
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-md text-base"
              data-testid="register-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear cuenta"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-[#94A3B8]">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-[#7AA2FF] font-medium hover:underline" data-testid="link-login">
              Iniciar sesión
            </Link>
          </p>
        </div>
      </div>

      {/* Hero side — Finly animated gradient (Trust Blue → Neon Green) */}
      <div className="hidden lg:block relative overflow-hidden finly-hero-gradient finly-hero-grid" data-testid="register-hero">
        <div className="finly-hero-orb" style={{ top: "10%", right: "-60px" }}></div>
        <div className="finly-hero-orb" style={{ bottom: "15%", left: "-80px", animationDelay: "4s" }}></div>
        <div className="absolute top-12 left-12 right-12 text-white/90 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[#00E590] animate-pulse"></span>
          <span className="text-[11px] uppercase tracking-[0.35em] font-medium">Únete a Finly+</span>
        </div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-display text-5xl xl:text-6xl font-black leading-[0.95] tracking-tight max-w-md">
            Tu dinero,<br/>
            <span className="text-[#00E590]">en orden.</span>
          </h2>
          <p className="mt-5 text-sm text-white/70 max-w-sm leading-relaxed">
            Comienza hoy a ver claro tus ingresos y gastos. Cuenta gratis, sin tarjeta.
          </p>
        </div>
      </div>
    </div>
  );
}
